import type CadenceStepOption from '@poseidon/types/pg-gen/cadenceStepOption';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import type { CadenceGraph, CadenceGraphNode } from '@poseidon/types/svr/svrCadenceTemplate.type';
import type { TreeStep } from '@poseidon/types/types/treeStep.type';

const stepTypeToOrder: { [K in CadenceStepType]: number } = {
  visit: 1,
  follow: 2,
  reminder: 3,
  call: 5,
  email: 6,
  likeRecentPosts: 8,
  connect: 9,
  inmail: 10,
  message: 11,
  delay: 12,
  wait_for_connect: 13,
  voice_note: 14,
  sift: 15,
};

export function cadenceStepBuildGraph<
  S extends {
    id: string;
    parent_id: string | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): CadenceGraph<S> | null {
  let startId: string | undefined;

  const filteredSteps = steps.filter((s) => s.archived == false || s.archived === undefined);

  const nodes: Record<string, CadenceGraphNode<S>> = {};
  filteredSteps
    .map((s) => Object.assign({}, s, { next: {}, group: [] }))
    .forEach((s) => {
      nodes[s.id] = s;
    });

  // Tail of each wait_for_connect branch chain, keyed by
  // `${wfcId}:${option}`. A wait_for_connect can have MULTIPLE sibling
  // children under the same option (e.g. several No-branch steps, all
  // parent=wfc, option='two') — but `next` has a single slot per
  // option, so writing `next[option]` for each sibling would overwrite
  // all but the last, orphaning the earlier steps. Instead we chain
  // them: the first child of a branch goes into `next[option]`, and each
  // subsequent sibling is linked onto the previous child via `next.one`.
  // Consumers (`walkChainFrom`, the campaign StepNodeRenderer) already
  // walk that `next.one` chain, so this makes every sibling render.
  // Steps arrive in `created_on` order, so the chain follows insertion
  // order — the sequence the user built.
  const wfcBranchTail: Record<string, string> = {};

  filteredSteps.forEach((step) => {
    if (step.parent_id == null) {
      startId = step.id;
      return;
    }

    const parentNode = nodes[step.parent_id];
    if (parentNode?.type == 'delay') {
      if (step.type == 'delay' || step.type == 'wait_for_connect') {
        parentNode.next[step.option] = step.id;
      } else {
        parentNode.group.push(step.id);
      }
    }
    if (parentNode?.type === 'wait_for_connect') {
      const key = `${parentNode.id}:${step.option}`;
      const tailId = wfcBranchTail[key];
      if (tailId == null) {
        parentNode.next[step.option] = step.id; // first leaf of this branch
      } else {
        nodes[tailId]!.next.one = step.id; // chain onto the previous sibling
      }
      wfcBranchTail[key] = step.id;
    }
  });

  if (startId == null) return null;

  return {
    nodes,
    startId,
  };
}

export function cadenceStepBuildTree<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): TreeStep<S> | null {
  const rootNode = steps.find((s) => s.parent_id == null && (s.archived == false || s.archived === undefined));
  if (rootNode == null) return null;

  const childrenMap = new Map<ID, S[]>();
  steps.forEach((s) => {
    if (s.parent_id == null) return;

    let children = childrenMap.get(s.parent_id);
    if (children == null) {
      children = [];
      childrenMap.set(s.parent_id, children);
    }
    children.push(s);
  });

  // sort the children so the order by steps is the same
  childrenMap.forEach((value) => {
    value.sort((a, b) => {
      const orderA = stepTypeToOrder[a.type];
      const orderB = stepTypeToOrder[b.type];

      return orderA - orderB;
    });
  });

  const tree = _cadenceStepBuildHelper(rootNode, childrenMap);
  return tree;
}
function _cadenceStepBuildHelper<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(rootNode: S, childrenMap: Map<ID, S[]>): TreeStep<S> {
  const childrenNodes = childrenMap.get(rootNode.id);
  if (childrenNodes == null) {
    const treeNode: TreeStep<S> = { ...rootNode, children: [] };
    return treeNode;
  }

  const children = childrenNodes.map((n) => _cadenceStepBuildHelper(n, childrenMap));
  const nonArchived = children.filter((n) => n.archived !== true);
  const archived = children.filter((n) => n.archived === true);
  const treeNode: TreeStep<S> = { ...rootNode, children: nonArchived, archivedChildren: archived };
  return treeNode;
}

export function cadenceStepLeftListFromRawSteps<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): (S & { triggerStep?: S })[] {
  // if the steps all have the same parent and there are no delay steps
  // these are runnable steps, just return them
  const parentIds = Array.from(new Set(steps.map((s) => s.parent_id)));
  const delayStep = steps.find((s) => s.type == 'delay');

  if (parentIds.length == 1 && delayStep == null) {
    return steps;
  }

  const tree = cadenceStepBuildTree(steps);
  if (tree == null) return [];

  const leftList = cadenceStepTreeBuildLeftList(tree);
  return leftList;
}

export function cadenceStepTreeBuildLeftList<
  ID extends string | number,
  S extends { id: ID; parent_id: ID | null; option: CadenceStepOption; type: CadenceStepType },
>(tree: TreeStep<S>): (S & { triggerStep?: S })[] {
  const acc: (S & { triggerStep?: S })[] = [tree];

  _cadenceStepTreeBuildLeftListHelper(tree, acc);
  return acc;
}

export function _cadenceStepTreeBuildLeftListHelper<
  ID extends string | number,
  S extends { id: ID; parent_id: ID | null; option: CadenceStepOption; type: CadenceStepType },
>(tree: TreeStep<S>, acc: (S & { triggerStep?: S })[]) {
  const rightChild = tree.children.find((c) => c.option == 'two');
  if (rightChild != null) {
    // there is a right child, attach to previous as trigger step
    const accLen = acc.length;
    const lastAccVal = acc[accLen - 1];
    if (lastAccVal) {
      lastAccVal.triggerStep = { ...rightChild };
    }
  }

  for (const child of tree.children) {
    if (child.option != 'one') continue;
    if (child.type == 'wait_for_connect' || child.type == 'delay') continue;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, archivedChildren, ...childCopy } = child;
    acc.push(childCopy as unknown as S);
  }

  const next = tree.children.find((c) => c.type == 'delay' || c.type == 'wait_for_connect');

  if (next != null) {
    acc.push(next);
    _cadenceStepTreeBuildLeftListHelper(next, acc);
  }
}
